import { useState } from 'react';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { Checkbox } from 'primereact/checkbox';
import { Card } from 'primereact/card';

const EducationStep = ({ data, onUpdate }) => {
    const [education, setEducation] = useState(data.education || []);

    const degreeTypes = [
        { label: 'High School', value: 'high_school' },
        { label: 'Diploma', value: 'diploma' },
        { label: 'Bachelors', value: 'bachelors' },
        { label: 'Masters', value: 'masters' },
        { label: 'PhD', value: 'phd' },
        { label: 'Certification', value: 'certification' }
    ];

    const institutionTypes = [
        { label: 'University', value: 'university' },
        { label: 'College', value: 'college' },
        { label: 'School', value: 'school' },
        { label: 'Online', value: 'online' }
    ];

    const addEducation = () => {
        const newEdu = {
            institution_name: '',
            institution_type: 'university',
            degree_type: 'bachelors',
            field_of_study: '',
            grade: '',
            start_date: null,
            end_date: null,
            is_ongoing: false,
            activities: '',
            description: ''
        };

        const updated = [...education, newEdu];
        setEducation(updated);
        onUpdate({ education: updated });
    };

    const removeEducation = (index) => {
        const updated = education.filter((_, i) => i !== index);
        setEducation(updated);
        onUpdate({ education: updated });
    };

    const updateEducation = (index, field, value) => {
        const updated = [...education];
        updated[index] = { ...updated[index], [field]: value };

        // If marking as ongoing, clear end_date
        if (field === 'is_ongoing' && value === true) {
            updated[index].end_date = null;
        }

        setEducation(updated);
        onUpdate({ education: updated });
    };

    return (
        <div className="grid">
            <div className="col-12">
                <div className="flex justify-content-between align-items-center">
                    <div>
                        <h5 className="mt-0 mb-1">Education</h5>
                        <p className="text-600 mt-0">Add your educational qualifications</p>
                    </div>
                    <Button
                        label="Add Education"
                        icon="pi pi-plus"
                        onClick={addEducation}
                        className="p-button-outlined"
                    />
                </div>
            </div>

            {education.length === 0 && (
                <div className="col-12">
                    <div className="surface-100 border-round p-4 text-center">
                        <i className="pi pi-graduation-cap text-4xl text-400 mb-3"></i>
                        <p className="text-600 m-0">No education records added yet</p>
                        <p className="text-500 text-sm m-0">Click "Add Education" to get started</p>
                    </div>
                </div>
            )}

            {education.map((edu, index) => (
                <div key={index} className="col-12">
                    <Card>
                        <div className="flex justify-content-between align-items-center mb-3">
                            <h6 className="m-0">Education #{index + 1}</h6>
                            <Button
                                icon="pi pi-trash"
                                className="p-button-rounded p-button-text p-button-danger"
                                onClick={() => removeEducation(index)}
                            />
                        </div>

                        <div className="grid">
                            <div className="col-12 md:col-6">
                                <label htmlFor={`institution_name_${index}`} className="block mb-2">Institution Name <span className="text-red-500">*</span></label>
                                <InputText
                                    id={`institution_name_${index}`}
                                    name="institution_name"
                                    value={edu.institution_name || ''}
                                    onChange={(e) => updateEducation(index, 'institution_name', e.target.value)}
                                    placeholder="e.g., Mumbai University"
                                    className="w-full"
                                />
                            </div>

                            <div className="col-12 md:col-6">
                                <label htmlFor={`institution_type_${index}`} className="block mb-2">Institution Type</label>
                                <Dropdown
                                    id={`institution_type_${index}`}
                                    name="institution_type"
                                    value={edu.institution_type || 'university'}
                                    options={institutionTypes}
                                    onChange={(e) => updateEducation(index, 'institution_type', e.value)}
                                    className="w-full"
                                />
                            </div>

                            <div className="col-12 md:col-6">
                                <label htmlFor={`degree_type_${index}`} className="block mb-2">Degree Type <span className="text-red-500">*</span></label>
                                <Dropdown
                                    id={`degree_type_${index}`}
                                    name="degree_type"
                                    value={edu.degree_type || 'bachelors'}
                                    options={degreeTypes}
                                    onChange={(e) => updateEducation(index, 'degree_type', e.value)}
                                    className="w-full"
                                />
                            </div>

                            <div className="col-12 md:col-6">
                                <label htmlFor={`field_of_study_${index}`} className="block mb-2">Field of Study <span className="text-red-500">*</span></label>
                                <InputText
                                    id={`field_of_study_${index}`}
                                    name="field_of_study"
                                    value={edu.field_of_study || ''}
                                    onChange={(e) => updateEducation(index, 'field_of_study', e.target.value)}
                                    placeholder="e.g., Business Administration"
                                    className="w-full"
                                />
                            </div>

                            <div className="col-12 md:col-4">
                                <label htmlFor={`start_date_${index}`} className="block mb-2">Start Date</label>
                                <Calendar
                                    id={`start_date_${index}`}
                                    name="start_date"
                                    value={edu.start_date ? new Date(edu.start_date) : null}
                                    onChange={(e) => updateEducation(index, 'start_date', e.value)}
                                    dateFormat="dd/mm/yy"
                                    placeholder="Select date"
                                    className="w-full"
                                    maxDate={new Date()}
                                    showIcon
                                    readOnlyInput
                                />
                            </div>

                            <div className="col-12 md:col-4">
                                <label htmlFor={`end_date_${index}`} className="block mb-2">End Date</label>
                                <Calendar
                                    id={`end_date_${index}`}
                                    name="end_date"
                                    value={edu.end_date ? new Date(edu.end_date) : null}
                                    onChange={(e) => updateEducation(index, 'end_date', e.value)}
                                    dateFormat="dd/mm/yy"
                                    placeholder="Select date"
                                    className="w-full"
                                    maxDate={new Date()}
                                    disabled={edu.is_ongoing}
                                    showIcon
                                    readOnlyInput
                                />
                            </div>

                            <div className="col-12 md:col-4">
                                <label htmlFor={`grade_${index}`} className="block mb-2">Grade / GPA</label>
                                <InputText
                                    id={`grade_${index}`}
                                    name="grade"
                                    value={edu.grade || ''}
                                    onChange={(e) => updateEducation(index, 'grade', e.target.value)}
                                    placeholder="e.g., 3.8 GPA or First Class"
                                    className="w-full"
                                />
                            </div>

                            <div className="col-12">
                                <div className="flex align-items-center">
                                    <Checkbox
                                        inputId={`ongoing-${index}`}
                                        checked={edu.is_ongoing}
                                        onChange={(e) => updateEducation(index, 'is_ongoing', e.checked)}
                                    />
                                    <label htmlFor={`ongoing-${index}`} className="ml-2">Currently pursuing</label>
                                </div>
                            </div>

                            <div className="col-12">
                                <label htmlFor={`activities_${index}`} className="block mb-2">Activities & Societies</label>
                                <InputText
                                    id={`activities_${index}`}
                                    name="activities"
                                    value={edu.activities || ''}
                                    onChange={(e) => updateEducation(index, 'activities', e.target.value)}
                                    placeholder="e.g., Debate club, Student council"
                                    className="w-full"
                                />
                            </div>

                            <div className="col-12">
                                <label htmlFor={`edu_description_${index}`} className="block mb-2">Description</label>
                                <InputTextarea
                                    id={`edu_description_${index}`}
                                    name="edu_description"
                                    value={edu.description || ''}
                                    onChange={(e) => updateEducation(index, 'description', e.target.value)}
                                    placeholder="Any additional details..."
                                    rows={2}
                                    className="w-full"
                                />
                            </div>
                        </div>
                    </Card>
                </div>
            ))}
        </div>
    );
};

export default EducationStep;
